import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import type { AssetAssignment, Custodian } from '../api';
import { useEmprestar, useLocations } from '../hooks';
import { CustodianAutocomplete } from './CustodianAutocomplete';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
  onSuccess: (assignment: AssetAssignment) => void;
}

export function LoanModal({ open, assetId, onClose, onSuccess }: Props) {
  const mutation = useEmprestar(assetId);
  const locationsQuery = useLocations();
  const [custodian, setCustodian] = useState<Custodian | null>(null);
  const [expectedReturnAt, setExpectedReturnAt] = useState('');
  const [deliveredAt, setDeliveredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setCustodian(null);
    setDeliveredAt(new Date().toISOString().slice(0, 10));
    setExpectedReturnAt(''); setLocationId(''); setNotes(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!custodian) { setError('Selecione o responsável'); return; }
    if (!expectedReturnAt) { setError('Informe a data prevista de devolução'); return; }
    // Espelha as regras do backend (A2): deliveredAt não pode ser futuro,
    // expectedReturnAt tem que ser posterior à entrega.
    const deliveredDate = new Date(deliveredAt);
    const now = new Date();
    // Compara pelo início do dia (UI só tem input type=date) — tolerância
    // suficiente pra evitar falso-positivo por fuso.
    now.setHours(23, 59, 59, 999);
    if (deliveredDate.getTime() > now.getTime()) {
      setError('Data da entrega não pode estar no futuro.'); return;
    }
    if (new Date(expectedReturnAt) <= deliveredDate) {
      setError('Data prevista de devolução deve ser posterior à data da entrega.'); return;
    }
    try {
      const result = await mutation.mutateAsync({
        custodianId: custodian.id,
        expectedReturnAt: new Date(expectedReturnAt).toISOString(),
        deliveredAt: new Date(deliveredAt).toISOString(),
        locationId: locationId || undefined,
        notes: notes || undefined,
      });
      reset(); onSuccess(result); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao registrar empréstimo'));
    }
  }

  return (
    <>
      <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Registrar empréstimo temporário</Dialog.Title>
            <p className="mt-1 text-xs text-gray-500">
              O termo de responsabilidade será gerado com previsão de devolução. Fluxo de assinatura idêntico ao de uma entrega.
            </p>
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Responsável *</label>
                <CustodianAutocomplete value={custodian} onChange={setCustodian} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  label="Data da entrega *"
                  type="date"
                  value={deliveredAt}
                  onChange={(e) => setDeliveredAt(e.target.value)}
                  required
                />
                <TextInput
                  label="Previsão de devolução *"
                  type="date"
                  value={expectedReturnAt}
                  onChange={(e) => setExpectedReturnAt(e.target.value)}
                  required
                />
              </div>
              <AutocompleteSelect
                label="Localização (opcional)"
                options={[
                  { value: '', label: 'Sem alteração' },
                  ...(locationsQuery.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                ]}
                value={locationId}
                onChange={setLocationId}
              />
              <Textarea
                label="Observações (opcional)"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
                <Button
                  type="submit"
                  loading={mutation.isPending}
                  className="!bg-yellow-600 hover:!bg-yellow-700 focus:!ring-yellow-500"
                >
                  {mutation.isPending ? 'Registrando…' : 'Registrar empréstimo'}
                </Button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
